package org.zest.insan_360_website.pages.user.report_layout

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.icons.fa.FaCheck
import com.varabyte.kobweb.silk.components.icons.fa.FaDownload
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronRight
import com.varabyte.kobweb.silk.components.icons.fa.FaXmark
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text
import org.zest.insan_360_website.components.layouts.*
import org.zest.insan_360_website.components.widgets.*
import org.zest.insan_360_website.data.GRAPH_MEASUREMENT
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.data.page_states.UserPreferences
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.fetchTestUserInfo
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.isFetching
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.isLoading
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.isVerifying
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.needToFetch
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.selectedTemplate
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.selectedUser
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.templates
import org.zest.insan_360_website.data.page_states.dashboard.ReportDashPageState.testUsers
import org.zest.insan_360_website.data.page_states.dashboard.submitGraphForVerification
import org.zest.insan_360_website.model.GraphType
import org.zest.insan_360_website.model.TestSubmissionStatus
import org.zest.insan_360_website.model.TestUser
import org.zest.insan_360_website.model.UserRole
import org.zest.insan_360_website.utils.formatToReadableDateJS

@Composable
fun SuperAdminLayout() {
    ReportHandling()
}

@Composable
fun ReportHandling() {
    val scope = rememberCoroutineScope()
    PageContentContainer(
        Modifier
            .padding(0.px)
            .border { width(0.px) }
    ) {
        if (isFetching) {
            TestSubmissionDetailSkeleton()
        } else if (selectedUser != null) {
            TestSubmissionDetail()
        } else {
            Column(Modifier.fillMaxSize().gap(0.5.cssRem)) {
                TestSubmissionsList(scope)
            }
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
fun TestSubmissionDetailSkeleton() {
    val breakpoint = rememberBreakpoint()

    Column(
        Modifier
            .fillMaxSize()
            .position(Position.Relative)
            .gap(2.cssRem)
    ) {
        Box(
            Modifier
                .fillMaxWidth()
                .aspectRatio(if (breakpoint < Breakpoint.MD) 1f else 2f)
                .backgroundColor(userPageBorderStrongColor.copyf(alpha = 0.3f))
                .borderRadius(1.cssRem)
                .styleModifier { property("animation", "pulse 1.5s infinite ease-in-out") }
        )

        if (breakpoint < Breakpoint.MD) {
            Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
                SkeletonBox(Modifier.fillMaxWidth().height(200.px))
                SkeletonBox(Modifier.fillMaxWidth().height(150.px))
            }
        } else {
            Row(
                Modifier.fillMaxWidth().gap(1.cssRem),
                verticalAlignment = Alignment.Top
            ) {
                SkeletonBox(Modifier.flex(2).height(250.px))
                SkeletonBox(Modifier.flex(1).minWidth(250.px).height(250.px))
            }
        }
    }
}

@Composable
private fun SkeletonBox(modifier: Modifier) {
    Box(
        modifier
            .backgroundColor(userPageBorderStrongColor.copyf(alpha = 0.3f))
            .borderRadius(1.cssRem)
            .styleModifier { property("animation", "pulse 1.5s infinite ease-in-out") }
    )
}

@Composable
fun TestSubmissionDetail() {
    Column(
        Modifier
            .fillMaxSize()
            .position(Position.Relative)
            .gap(2.cssRem)
    ) {
        TestGraph()
        DetailForVerification()
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun TestGraph() {
    Box(
        contentButtonStyle.toModifier()
            .position(Position.Absolute)
            .aspectRatio(1f)
            .size(2.cssRem)
            .right(8.px)
            .top(8.px)
            .onClick {
                selectedUser = null
                selectedTemplate = null
            },
        contentAlignment = Alignment.Center
    ) {
        FaXmark(contentButtonTextStyle.toModifier(), IconSize.LG)
    }
    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        DiscGraphs(
            measurements = GRAPH_MEASUREMENT.addScale(3),
            plotPointsMore = selectedUser!!.answers.graphs[GraphType.More]!!,
            plotPointsLess = selectedUser!!.answers.graphs[GraphType.Less]!!,
            plotPointsDifference = selectedUser!!.answers.graphs[GraphType.Difference]!!
        )

        DiscGraphs(
            measurements = GRAPH_MEASUREMENT.addScale(3),
            plotPointsMore = selectedUser!!.answers.graphs[GraphType.More]!!,
            plotPointsLess = selectedUser!!.answers.graphs[GraphType.Less]!!,
            plotPointsDifference = selectedUser!!.answers.graphs[GraphType.Difference]!!,
            isForPrint = true
        )
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun DetailForVerification() {
    val breakpoint = rememberBreakpoint()

    if (breakpoint < Breakpoint.MD) {
        Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
            StudentDetail(Modifier.fillMaxWidth())
            VerificationSection(Modifier.fillMaxWidth())
        }
    } else {
        Row(
            Modifier.fillMaxWidth().gap(1.cssRem),
            verticalAlignment = Alignment.Top
        ) {
            StudentDetail(Modifier.flex(2).minWidth(0.px))
            VerificationSection(Modifier.flex(1).minWidth(250.px))
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
fun VerificationSection(modifier: Modifier = Modifier) {
    PageContentContainer(modifier.then(containerShadowStyle.toModifier())) {
        Column(
            Modifier
                .fillMaxSize()
                .padding(1.5.cssRem)
                .gap(1.cssRem)
        ) {
            TemplateSelect()
            Box(
                Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(userPageBorderStrongColor)
            )
            Column(
                Modifier
                    .fillMaxWidth()
                    .gap(0.75.cssRem)
                    .fillMaxHeight(),
                verticalArrangement = Arrangement.Center,
            ) {
                val testUser = selectedUser?.let { selected -> testUsers?.users?.find { it.testId == selected.testId } }
                if (testUser?.status == TestSubmissionStatus.PENDING) {
                    VerifySubmissionButton()
                    RejectSubmissionButton()
                } else if (testUser?.status == TestSubmissionStatus.VERIFIED) {
                    val nowMillis = kotlin.js.Date.now()
                    val expiresAtMillis = testUser.reportExpiresAt?.toEpochMilliseconds()?.toDouble() ?: 0.0

                    if (expiresAtMillis > nowMillis) {
                        DownloadAdminReportButton(testUser)
                        SpanText(
                            text = LanguageSettings.UserDashboardPage.ReportDetail.report_expires_on.localized + formatToReadableDateJS(testUser.reportExpiresAt!!),
                            modifier = Modifier.fillMaxWidth().fontSize(0.75.cssRem).color(userPageSecondaryTextColor).textAlign(TextAlign.Center)
                        )
                    } else {
                        VerifySubmissionButton(isRegeneration = true)
                        if (testUser.reportRequested) {
                            SpanText(
                                text = LanguageSettings.UserDashboardPage.ReportDetail.user_requested.localized,
                                modifier = Modifier.fillMaxWidth().fontSize(0.75.cssRem).color(Colors.Orange).textAlign(TextAlign.Center).fontWeight(FontWeight.Bold)
                            )
                        }
                    }
                } else {
                    SpanText(
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(1.cssRem)
                            .fontWeight(FontWeight.SemiBold)
                            .fontStyle(FontStyle.Italic)
                            .color(userPageSecondaryTextColor)
                            .textAlign(TextAlign.Center),
                        text = LanguageSettings.UserDashboardPage.ReportDetail.view_only.localized
                    )
                }
            }
        }
    }
}

@Composable
private fun VerifySubmissionButton(isRegeneration: Boolean = false) {
    val scope = rememberCoroutineScope()
    val toast = LocalToast.current

    Box(
        contentButtonStyle.toModifier()
            .fillMaxWidth()
            .border {
                width(1.px)
                style(LineStyle.Solid)
                color(userPageColor.darkened(0.25f))
            }
            .padding(topBottom = 0.75.cssRem)
            .borderRadius(0.5.cssRem)
            .cursor(if (isVerifying) Cursor.NotAllowed else Cursor.Pointer)
            .opacity(if (isVerifying) 0.7 else 1.0)
            .onClick {
                if (!isVerifying) {
                    submitGraphForVerification(
                        AuthState.email ?: "",
                        "disc-graphs-print",
                        selectedTemplate ?: "",
                        selectedUser!!.testId,
                        selectedUser!!.name,
                        selectedUser!!.ic,
                        scope,
                        toast
                    ) {
                        selectedUser = null
                        selectedTemplate = null
                        needToFetch = true
                    }
                }
            },
        contentAlignment = Alignment.Center
    ) {
        if (isVerifying) {
            DotLoading(userPagePrimaryTextColor)
        } else {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
                FaCheck(contentButtonTextStyle.toModifier().fontSize(1.25.cssRem))
                SpanText(
                    if (isRegeneration) LanguageSettings.UserDashboardPage.ReportDetail.generate_report.localized
                    else LanguageSettings.UserDashboardPage.ReportDetail.verify.localized,
                    contentButtonTextStyle.toModifier()
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                )
            }
        }
    }
}

@Composable
private fun DownloadAdminReportButton(testUser: TestUser) {
    val scope = rememberCoroutineScope()
    val toast = LocalToast.current

    Box(
        contentButtonStyle.toModifier()
            .fillMaxWidth()
            .border {
                width(1.px)
                style(LineStyle.Solid)
                color(userPageColor.darkened(0.25f))
            }
            .padding(topBottom = 0.75.cssRem)
            .borderRadius(0.5.cssRem)
            .cursor(Cursor.Pointer)
            .onClick {
                ReportDashPageState.downloadVerifiedReport(testUser.testId, toast, scope)
            },
        contentAlignment = Alignment.Center
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
            FaDownload(contentButtonTextStyle.toModifier().fontSize(1.25.cssRem))
            SpanText(
                LanguageSettings.UserDashboardPage.download_report.localized,
                contentButtonTextStyle.toModifier()
                    .fontSize(1.25.cssRem)
                    .fontWeight(FontWeight.Bold)
            )
        }
    }
}

@Composable
private fun RejectSubmissionButton() {
    val scope = rememberCoroutineScope()
    val toast = LocalToast.current

    Box(
        contentRedButtonStyle.toModifier()
            .fillMaxWidth()
            .border {
                width(1.px)
                style(LineStyle.Solid)
                color(userPageColor.darkened(0.25f))
            }
            .padding(topBottom = 0.75.cssRem)
            .borderRadius(0.5.cssRem)
            .cursor(if (isVerifying) Cursor.NotAllowed else Cursor.Pointer)
            .opacity(if (isVerifying) 0.7 else 1.0)
            .onClick {
                if (!isVerifying && selectedUser != null) {
                    org.zest.insan_360_website.data.page_states.dashboard.rejectSubmission(
                        AuthState.email ?: "",
                        selectedUser!!.testId,
                        scope,
                        toast
                    ) {
                        selectedUser = null
                        selectedTemplate = null
                        needToFetch = true
                    }
                }
            },
        contentAlignment = Alignment.Center
    ) {
        if (isVerifying) {
            DotLoading(userPagePrimaryTextColor)
        } else {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
                FaXmark(contentButtonTextStyle.toModifier().fontSize(1.25.cssRem))
                SpanText(
                    LanguageSettings.UserDashboardPage.ReportDetail.reject.localized,
                    contentButtonTextStyle.toModifier()
                        .fontSize(1.25.cssRem)
                        .fontWeight(FontWeight.Bold)
                )
            }
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun TemplateSelect() {
    val modal = LocalModal.current
    Box(
        neutralContentButtonStyle.toModifier()
            .fillMaxWidth()
            .padding(topBottom = 1.cssRem, leftRight = 0.5.cssRem)
            .border {
                width(1.px)
                style(LineStyle.Solid)
                color(userPageColor.darkened(0.25f))
            }
            .borderRadius(0.5.cssRem)
            .cursor(Cursor.Pointer)
            .onClick {
                modal.show { TemplateSelectionModalContent() }
            },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            selectedTemplate ?: LanguageSettings.UserDashboardPage.ReportDetail.select_template.localized,
            Modifier
                .fontSize(1.25.cssRem)
                .color(userPagePrimaryTextColor.copyf(alpha = 0.75f))
                .fontWeight(FontWeight.SemiBold)
                .textAlign(TextAlign.Center)
        )
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun StudentDetail(modifier: Modifier = Modifier) {
    val breakpoint = rememberBreakpoint()

    PageContentContainer(
        modifier
            .fillMaxWidth()
            .fontSize(1.cssRem)
            .padding(1.5.cssRem)
            .then(containerShadowStyle.toModifier())
    ) {
        Column(
            Modifier
                .fillMaxSize()
                .gap(1.5.cssRem)
        ) {
            if (breakpoint < Breakpoint.XL) {
                Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                    TestIdBadge()
                    NameAndEmailSection(Modifier.fillMaxWidth())
                }
            } else {
                Row(
                    Modifier.fillMaxWidth().gap(1.cssRem),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top
                ) {
                    NameAndEmailSection(Modifier.flex(1).minWidth(0.px))
                    TestIdBadge(Modifier.flexShrink(0))
                }
            }
            Box(
                Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(userPageBorderStrongColor)
            )
            StudentDataRow(
                value = selectedUser?.ic
                    ?: LanguageSettings.UserDashboardPage.ReportDetail.identification_number.localized,
                label = LanguageSettings.UserDashboardPage.ReportDetail.identification_number.localized
            )
            StudentDataRow(
                value = selectedUser?.phone ?: LanguageSettings.UserDashboardPage.ReportDetail.phone_number.localized,
                label = userDashWordLibrary.phone_number.localized
            )
            StudentDataRow(
                value = if (selectedUser != null) formatToReadableDateJS(selectedUser!!.submittedAt) else LanguageSettings.UserDashboardPage.ReportDetail.date.localized,
                label = userDashWordLibrary.date_answered.localized
            )
        }
    }
}

@Composable
private fun NameAndEmailSection(modifier: Modifier = Modifier) {
    Column(modifier.gap(0.2.cssRem)) {
        SpanText(
            selectedUser?.name ?: userDashWordLibrary.name.localized,
            Modifier
                .fontSize(1.5.cssRem)
                .fontWeight(FontWeight.Bold)
                .color(userPagePrimaryTextColor)
        )
        SpanText(
            selectedUser?.email ?: userDashWordLibrary.email.localized,
            Modifier
                .fontSize(1.cssRem)
                .fontWeight(FontWeight.Light)
                .color(userPageSecondaryTextColor)
        )
    }
}

@Composable
private fun TestIdBadge(modifier: Modifier = Modifier) {
    Box(
        modifier
            .backgroundColor(userPageBorderStrongColor.copyf(alpha = 0.5f))
            .padding(leftRight = 0.75.cssRem, topBottom = 0.25.cssRem)
            .borderRadius(1.cssRem)
    ) {
        SpanText(
            selectedUser?.testId ?: "Test ID",
            Modifier
                .fontSize(0.75.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .color(userPagePrimaryTextColor)
        )
    }
}

@Composable
private fun StudentDataRow(value: String, label: String) {
    Column(Modifier.gap(0.2.cssRem)) {
        SpanText(
            value,
            Modifier
                .fontSize(1.35.cssRem)
                .fontWeight(FontWeight.Bold)
                .color(userPagePrimaryTextColor)
        )
        SpanText(
            label,
            Modifier
                .fontSize(1.cssRem)
                .fontWeight(FontWeight.Normal)
                .color(userPageSecondaryTextColor)
        )
    }
}

@OptIn(DelicateApi::class)
@Composable
fun TestSubmissionsList(scope: CoroutineScope) {
    val toast = LocalToast.current
    val breakpoint = rememberBreakpoint()
    val isSuperAdmin = AuthState.role == UserRole.SUPER_ADMIN

    TableFlushedListContainer(
        1,
        header = {
            Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
                if (breakpoint >= Breakpoint.MD) {
                    Row(
                        Modifier.fillMaxWidth().gap(0.5.cssRem),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        SpanText(
                            userDashWordLibrary.report.localized,
                            Modifier.fontWeight(FontWeight.Bold).fontSize(1.5.cssRem).whiteSpace(WhiteSpace.NoWrap)
                        )
                        Row(
                            Modifier.fillMaxWidth().justifyContent(JustifyContent.FlexEnd).gap(0.5.cssRem),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            TabsAndFilterInputs()
                        }
                    }
                } else {
                    Column(
                        Modifier.fillMaxWidth().gap(0.5.cssRem)
                    ) {
                        SpanText(
                            userDashWordLibrary.report.localized,
                            Modifier.fontWeight(FontWeight.Bold).fontSize(1.5.cssRem).whiteSpace(WhiteSpace.NoWrap)
                        )
                        Row(
                            Modifier.fillMaxWidth().justifyContent(JustifyContent.FlexStart).gap(0.5.cssRem),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            TabsAndFilterInputs()
                        }
                    }
                }
            }
        },
        listHeaders = {
            ListHeader(
                "ID",
                modifier = Modifier
                    .position(Position.Sticky)
                    .left(0.5.cssRem)
                    .zIndex(10)
                    .backgroundColor(userPageBGColor)
                    .styleModifier { property("box-shadow", "-0.5rem 0 0 0 $userPageBGColor") }
            )
            ListHeader(userDashWordLibrary.name.localized)
            ListHeader(userDashWordLibrary.email.localized)
            if (isSuperAdmin) ListHeader(userDashWordLibrary.organization_name.localized)
            ListHeader(userDashWordLibrary.phone_number.localized)
            ListHeader(userDashWordLibrary.date_answered.localized)
            ListHeader("Status")
            ListHeader("", true)
        },
        listHeadersModifier = Modifier
            .gap(0.25.cssRem)
            .grid {
                columns {
                    size(1.fr)
                    size(2.fr)
                    size(2.fr)
                    if (isSuperAdmin) size(2.fr)
                    size(1.5.fr)
                    size(1.5.fr)
                    size(1.fr)
                    size(24.px)
                }
            }
    ) {
        val filteredList = ReportDashPageState.getFilteredSubmissions()

        if (isLoading) {
            Box(Modifier.fillMaxWidth().minHeight(150.px).padding(1.cssRem), contentAlignment = Alignment.Center) {
                DotLoading()
            }
        } else if (filteredList.isEmpty()) {
            Box(Modifier.fillMaxWidth().minHeight(150.px).padding(1.cssRem), contentAlignment = Alignment.Center) {
                SpanText(
                    userDashWordLibrary.no_submissions.localized,
                    Modifier.color(userPageSecondaryTextColor).fontWeight(FontWeight.Medium)
                )
            }
        } else {
            var index = 0
            filteredList.forEach { testUser ->
                FlushedListRowWrapper(
                    index = index,
                    modifier = Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { fetchTestUserInfo(testUser, toast, scope) }
                ) {
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .minWidth(if (isSuperAdmin) 1800.px else 1200.px)
                            .gap(0.25.cssRem)
                            .grid {
                                columns {
                                    size(1.fr)
                                    size(2.fr)
                                    size(2.fr)
                                    if (isSuperAdmin) size(2.fr)
                                    size(1.5.fr)
                                    size(1.5.fr)
                                    size(1.fr)
                                    size(24.px)
                                }
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        val bgCol = if (index % 2 == 0) userPageColor else userPageBGColor
                        Box(
                            Modifier
                                .fillMaxWidth()
                                .position(Position.Sticky)
                                .left(0.5.cssRem)
                                .zIndex(2)
                                .backgroundColor(bgCol)
                                .styleModifier { property("box-shadow", "-0.5rem 0 0 0 $bgCol") }
                        ) {
                            CopyableIdText(testUser.testId)
                        }
                        ListEntry(testUser.name)
                        ListEntry(testUser.email)
                        if (isSuperAdmin) ListEntry(testUser.orgName ?: "-")
                        ListEntry(testUser.phone)
                        ListEntry(formatToReadableDateJS(testUser.submittedAt))
                        Box(Modifier.fillMaxWidth()) {
                            TestSubmissionStatusBadge(testUser.status)
                        }
                        Box(Modifier.fillMaxWidth().aspectRatio(1), contentAlignment = Alignment.Center) {
                            FaChevronRight(
                                Modifier
                                    .color(userPageSecondaryTextColor)
                                    .fontSize(0.8.cssRem)
                            )
                        }
                    }
                }
                index++
            }
        }
    }
}


@OptIn(DelicateApi::class)
@Composable
private fun TabsAndFilterInputs() {
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint < Breakpoint.MD

    Row(
        Modifier.gap(0.5.cssRem).then(if (isMobile) Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap) else Modifier),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            Modifier
                .backgroundColor(userPageBorderStrongColor.copyf(alpha = 0.5f))
                .borderRadius(0.25.cssRem)
                .padding(0.25.cssRem)
                .gap(0.25.cssRem)
                .alignItems(AlignItems.Center)
        ) {
            Box(
                Modifier
                    .padding(leftRight = 0.75.cssRem, topBottom = 0.3.cssRem)
                    .borderRadius(0.2.cssRem)
                    .cursor(Cursor.Pointer)
                    .backgroundColor(if (ReportDashPageState.activeTab == ReportDashPageState.TestSubmissionsTab.LATEST) userPageColor else Colors.Transparent)
                    .onClick { ReportDashPageState.activeTab = ReportDashPageState.TestSubmissionsTab.LATEST }
            ) {
                SpanText(
                    userDashWordLibrary.latest.localized,
                    Modifier.fontSize(0.85.cssRem).fontWeight(FontWeight.Medium)
                )
            }
            Box(
                Modifier
                    .padding(leftRight = 0.75.cssRem, topBottom = 0.3.cssRem)
                    .borderRadius(0.2.cssRem)
                    .cursor(Cursor.Pointer)
                    .backgroundColor(if (ReportDashPageState.activeTab == ReportDashPageState.TestSubmissionsTab.PAST) userPageColor else Colors.Transparent)
                    .onClick { ReportDashPageState.activeTab = ReportDashPageState.TestSubmissionsTab.PAST }
            ) {
                SpanText(
                    userDashWordLibrary.history.localized,
                    Modifier.fontSize(0.85.cssRem).fontWeight(FontWeight.Medium)
                )
            }
        }

        Select(
            Modifier
                .fontFamily("FunnelDisplay")
                .fontWeight(FontWeight.SemiBold)
                .padding(0.25.cssRem)
                .borderRadius(0.25.cssRem)
                .border {
                    width(1.px)
                    style(LineStyle.Solid)
                    color(userPageBorderStrongColor)
                }
                .backgroundColor(userPageBGColor)
                .color(userPagePrimaryTextColor)
                .outline(0.px)
                .cursor(Cursor.Pointer)
                .toAttrs {
                    onInput { e ->
                        e.value?.let {
                            ReportDashPageState.statusFilter = ReportDashPageState.TestSubmissionFilter.valueOf(it)
                        }
                    }
                }
        ) {
            ReportDashPageState.TestSubmissionFilter.entries.forEach { option ->
                Option(
                    value = option.name,
                    attrs = {
                        if (option == ReportDashPageState.statusFilter) selected()
                    }
                ) {
                    val statusLabel =
                        userDashWordLibrary.testSubmissionFilterOptions[option.name]?.get(UserPreferences.language)
                            ?: option.name
                    Text(statusLabel)
                }
            }
        }
        Input(
            org.jetbrains.compose.web.attributes.InputType.Text,
            attrs = Modifier
                .flex(1)
                .then(if (isMobile) Modifier else Modifier.maxWidth(480.px))
                .fontFamily("FunnelDisplay")
                .fontWeight(FontWeight.SemiBold)
                .padding(0.325.cssRem)
                .borderRadius(0.25.cssRem)
                .border {
                    width(1.px)
                    style(LineStyle.Solid)
                    color(userPageBorderStrongColor)
                }
                .backgroundColor(userPageBGColor)
                .color(userPagePrimaryTextColor)
                .outline(0.px)
                .toAttrs {
                    value(ReportDashPageState.searchQuery)
                    onInput { ReportDashPageState.searchQuery = it.value }
                    attr("placeholder", userDashWordLibrary.search.localized)
                }
        )
    }
}

@Composable
fun TestSubmissionStatusBadge(status: TestSubmissionStatus) {
    val statusColor = when (status) {
        TestSubmissionStatus.VERIFIED -> Color.rgb(0x16a34a)
        TestSubmissionStatus.PENDING -> Color.rgb(0xd97706)
        TestSubmissionStatus.REJECTED -> Color.rgb(0xdc2626)
    }

    val displayText =
        userDashWordLibrary.testSubmissionState[status]?.get(UserPreferences.language)
            ?: status.name

    StatusBadge(
        text = displayText,
        color = statusColor
    )
}

@Composable
private fun TemplateSelectionModalContent() {
    val modal = LocalModal.current

    Box(
        Modifier
            .fillMaxWidth()
            .maxHeight(80.vh)
            .overflow(Overflow.Auto)
            .userSelect(UserSelect.None)
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .flexWrap(FlexWrap.Wrap)
                .gap(0.5.cssRem),
            horizontalArrangement = Arrangement.Center,
        ) {
            templates.forEach { template ->
                Box(
                    neutralContentButtonStyle.toModifier()
                        .size(6.cssRem)
                        .border {
                            width(1.px)
                            color(userPageBorderStrongColor)
                            style(LineStyle.Solid)
                        }
                        .borderRadius(0.5.cssRem)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            selectedTemplate = template
                            modal.hide()
                        },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        template,
                        Modifier
                            .fontSize(0.85.cssRem)
                            .fontWeight(FontWeight.Medium)
                            .textAlign(TextAlign.Center),
                    )
                }
            }
        }
    }
}